import { useTranslation } from '@shared/core';
import globalWindow from '@shared/core/globals';
import { isPast } from 'date-fns';
import { useRef, useState, useEffect } from 'react';

interface CountdownState
  extends Readonly<{
    days?: number;
    hrs?: number;
    mins?: number;
  }> {}

const CountdownDefaultState: CountdownState = {
  days: 0,
  hrs: 0,
  mins: 0,
} as const;

function getTimeDifference(date1: Date, date2: Date) {
  // Convert both dates to milliseconds
  const diffMs = Math.abs(date2.getTime() - date1.getTime());

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diffMs / (1000 * 60)) % 60);

  return { days, hours, minutes };
}

/**
 * create event count down object
 * if event in the past then time will increase
 * if event in the future then time will decrease
 */
function _createTimerDuration(startDate: Date | number): CountdownState {
  const duration = getTimeDifference(new Date(startDate), new Date());

  return {
    days: duration.days,
    hrs: duration.hours,
    mins: duration.minutes,
  };
}

export const useCountdown = (milliseconds: number = 0) => {
  const hasPassedRef = useRef<boolean>(isPast(milliseconds));
  const [countDown, setCountdown] = useState<CountdownState>(_createTimerDuration(milliseconds) || CountdownDefaultState);
  const { t } = useTranslation('joykit');
  const eventCountDownTranslations = t('eventCountdown');

  useEffect(() => {
    const interval = globalWindow.setInterval(() => {
      hasPassedRef.current = isPast(milliseconds);
      setCountdown(_createTimerDuration(milliseconds));
    }, 10000); // Update every 10 seconds
    return function cleanup() {
      globalWindow.clearInterval(interval);
    };
  }, [milliseconds]);

  const durationToDisplay = hasPassedRef.current ? eventCountDownTranslations.displayTextAgo(countDown) : eventCountDownTranslations.displayText(countDown);
  return {
    formattedDuration: durationToDisplay,
    countDown,
  };
};
